import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Theme } from '@radix-ui/themes'
import { Menu } from 'lucide-react'
import { OnboardingSidebar } from './OnboardingSidebar'
import { useMe, isAccountBlocked, isSessionExpired, getErrorMessage } from '../hooks/useMe'
import { LoadingSpinner } from './LoadingSpinner'
import { AuthErrorScreen } from './AuthErrorScreen'
import { ToastProvider } from '../context/ToastProvider'
import { ToastContainer } from './ToastContainer'

export function TenantSlugLayout() {
  const { data: meData, isLoading, error } = useMe()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (isLoading) {
    return <LoadingSpinner message="Loading tenant..." />
  }

  // Account blocked — don't render error UI, global overlay handles it
  if (error && isAccountBlocked(error)) {
    return <LoadingSpinner message="Loading tenant (blocked)..." />
  }

  // Session expired / revoked — don't render error UI, toast + redirect handles it
  if (error && isSessionExpired(error)) {
    return <LoadingSpinner message="Loading tenant (session)..." />
  }

  if (error) {
    return (
      <AuthErrorScreen
        type="unknown_error"
        message={getErrorMessage(error)}
        actionLabel="Back to sign in"
        actionHref="/auth/login"
      />
    )
  }

  // User has no tenant — show message per spec
  if (meData && !meData.tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No organisation found</h2>
          <p className="text-sm text-gray-600 mb-6">
            Your account is not associated with any organisation. Please contact your administrator or support for assistance.
          </p>
          <a
            href="/auth/logout"
            className="inline-block px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800"
          >
            Sign out
          </a>
        </div>
      </div>
    )
  }

  return (
    <ToastProvider>
      <ToastContainer />
      <Theme appearance="light" accentColor="gray" radius="medium">
        <div className="min-h-screen bg-gray-50">
          {/* Desktop Sidebar - hidden on mobile, visible on lg+ */}
          <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:z-50">
            <OnboardingSidebar />
          </div>

          {/* Mobile Sidebar - only rendered when open */}
          {sidebarOpen && (
            <>
              {/* Overlay */}
              <div
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
              />
              {/* Sidebar */}
              <div className="fixed inset-y-0 left-0 w-64 z-50 lg:hidden">
                <OnboardingSidebar onClose={() => setSidebarOpen(false)} />
              </div>
            </>
          )}

          {/* Main Content — h-screen so <main> becomes a bounded internal
              scroll container (lets sticky page headers pin to the top). */}
          <div className="lg:pl-64 h-screen flex flex-col">
            {/* Mobile Header with hamburger menu */}
            <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                {meData?.tenant?.logo_url ? (
                  <img
                    src={meData.tenant.logo_url}
                    alt={meData.tenant.name}
                    className="h-7 w-7 object-contain rounded"
                  />
                ) : (
                  <div className="h-7 w-7 rounded bg-brand-primary flex items-center justify-center">
                    <span className="text-xs font-bold text-[var(--brand-primary-text)]">
                      {meData?.tenant?.name?.[0] || 'S'}
                    </span>
                  </div>
                )}
                <span className="font-semibold text-gray-900 text-sm">
                  {meData?.tenant?.name || 'SupplyScanner'}
                </span>
              </div>
            </header>

            {/* Page Content — min-h-0 lets flex-1 + overflow-y-auto scroll
                internally instead of growing the page (window scroll). */}
            <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 lg:p-8">
              <Outlet />
            </main>
          </div>
        </div>
      </Theme>
    </ToastProvider>
  )
}
